// src/components/VideoGallery.jsx
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import VideoPlayer from './VideoPlayer';
import ThumbnailList from './ThumbnailList';

const SITE = 'https://new-nightlife.jp';

// GA4 送信用（存在時のみ）
const trackGA = (event, params = {}) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event, params);
    }
  } catch {}
};

const toIso = (v) => {
  if (!v) return new Date().toISOString();
  if (typeof v === 'string') return v;
  if (v?.toDate) return v.toDate().toISOString(); // Firestore Timestamp
  return new Date(v).toISOString();
};

const VideoGallery = () => {
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userLoaded, setUserLoaded] = useState(false);

  const videoRef = useRef(null);
  const urlCacheRef = useRef(new Map());
  const videoCacheRef = useRef(null);

  // 認証状態の判定（完了したら取得続行）
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => setUserLoaded(true));
    return () => unsub();
  }, []);

  // Firestore から動画一覧を取得（署名URLを並列生成・キャッシュ）
  useEffect(() => {
    if (!userLoaded) return;
    let alive = true;

    const getSignedUrl = async (path) => {
      if (!path) return '';
      const cache = urlCacheRef.current;
      if (cache.has(path)) return cache.get(path);
      try {
        const signed = await getDownloadURL(ref(storage, path));
        cache.set(path, signed);
        return signed;
      } catch {
        return '';
      }
    };

    const fetchVideos = async () => {
      setLoading(true);
      try {
        if (videoCacheRef.current) {
          if (alive) {
            setVideos(videoCacheRef.current);
            setCurrentIndex(0);
          }
          return;
        }

        const videosQuery = query(collection(db, 'videos'), orderBy('uploadedAt', 'desc'));
        const snapshot = await getDocs(videosQuery);
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        const fetched = await Promise.all(
          docs
            .filter((v) => !!v.fileUrl)
            .map(async (v) => {
              const [fileUrl, thumbUrlSigned] = await Promise.all([
                getSignedUrl(v.fileUrl),
                v.thumbUrl ? getSignedUrl(v.thumbUrl) : Promise.resolve(''),
              ]);
              return {
                ...v,
                fileUrl,
                thumbUrl: thumbUrlSigned || 'https://placehold.co/160x90?text=No+Image',
              };
            }),
        );

        const valid = fetched.filter((v) => !!v.fileUrl);
        videoCacheRef.current = valid;
        if (alive) {
          setVideos(valid);
          setCurrentIndex(0);
        }
      } catch (e) {
        console.error('Firestore取得エラー:', e?.message || e);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchVideos();
    return () => {
      alive = false;
    };
  }, [userLoaded]);

  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % videos.length);
  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);

  const selected = videos[currentIndex];
  const nextVideo = videos.length > 0 ? videos[(currentIndex + 1) % videos.length] : null;

  // GAイベントだけ付与（views 加算は VideoPlayer 内で bumpVideoView を実行）
  useEffect(() => {
    const el = videoRef.current;
    if (!selected || !el) return;

    const onPlay = () =>
      trackGA('video_play', {
        video_id: selected.id,
        title: selected.title,
        duration: selected.duration || 'PT1M',
      });
    const onPause = () => trackGA('video_pause', { video_id: selected.id });
    const onEnded = () => trackGA('video_complete', { video_id: selected.id });

    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [selected]);

  // JSON-LD
  const videoJsonLd =
    selected && {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: selected.title || '動画',
      description: selected.description || 'New-Nightlife の動画コンテンツ',
      thumbnailUrl: [selected.thumbUrl].filter(Boolean),
      contentUrl: selected.fileUrl,
      uploadDate: toIso(selected.uploadedAt),
      duration: selected.duration || 'PT1M',
      keywords: (selected.tags || []).join(', '),
      publisher: { '@type': 'Organization', name: 'New-Nightlife' },
    };

  return (
    <div style={styles.container}>
      {selected && (
        <Helmet>
          <title>{`${selected.title || '動画'}｜New-Nightlife`}</title>
          <meta
            name="description"
            content={(selected.description || 'New-Nightlife の動画コンテンツ').slice(0, 100)}
          />
          <link rel="canonical" href={`${SITE}/#video-${selected.id}`} />
          {selected.fileUrl && <link rel="preload" as="video" href={selected.fileUrl} />}
          {nextVideo?.fileUrl && <link rel="prefetch" as="video" href={nextVideo.fileUrl} />}
          <link rel="preconnect" href="https://firebasestorage.googleapis.com" crossOrigin="" />
          <script type="application/ld+json">{JSON.stringify(videoJsonLd)}</script>
        </Helmet>
      )}

      {loading ? (
        <p style={styles.message} role="status" aria-live="polite">
          ⏳ 読み込み中です...
        </p>
      ) : videos.length === 0 ? (
        <div style={styles.emptyWrapper} aria-live="polite">
          <div style={styles.dummyVideoBox}>
            <p style={styles.dummyText}>🎥 動画プレーヤー枠</p>
          </div>
          <div style={styles.dummyThumbnailGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={styles.dummyThumbnail}>
                <p style={styles.dummyText}>No.{i + 1}</p>
              </div>
            ))}
          </div>
          <p style={styles.emptyMessage}>🎬 現在、表示できる動画がありません。</p>
          <p style={styles.emptySub}>アップロードをお待ちください。</p>
        </div>
      ) : (
        <>
          <VideoPlayer
            videoRef={videoRef}                 // ← 外部refをVideoPlayerに渡す
            videoUrl={selected.fileUrl}
            videoId={selected.id}               // ← v.id のバグ修正
            thumbnailUrl={selected.thumbUrl}
            onNext={handleNext}
            onPrev={handlePrev}
            preloadNextUrl={nextVideo?.fileUrl}
            title={selected.title}
            description={selected.description}
            tags={selected.tags}
            uploadedAt={selected.uploadedAt}
            duration={selected.duration}
          />
          <ThumbnailList
            thumbnails={videos}
            selectedIndex={currentIndex}
            onSelect={setCurrentIndex}
          />
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '12px 16px',
    textAlign: 'center',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  message: {
    fontSize: '15px',
    color: '#999',
    padding: '20px',
    fontStyle: 'italic',
  },
  emptyWrapper: {
    padding: '24px 12px',
    textAlign: 'center',
  },
  dummyVideoBox: {
    width: '100%',
    maxWidth: '480px',
    aspectRatio: '16 / 9',
    backgroundColor: '#f0f0f0',
    margin: '0 auto 20px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  },
  dummyThumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
    gap: '10px',
    maxWidth: '480px',
    margin: '0 auto 20px',
  },
  dummyThumbnail: {
    backgroundColor: '#e0e0e0',
    height: '60px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    color: '#777',
    fontStyle: 'italic',
  },
  dummyText: {
    fontSize: '13px',
    color: '#999',
  },
  emptyMessage: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#e91e63',
    marginBottom: '6px',
  },
  emptySub: {
    fontSize: '14px',
    color: '#666',
  },
};

export default VideoGallery;
